import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, MessageSquareIcon, PhoneIcon, BellIcon, GlobeIcon, Share2Icon, MonitorIcon, WifiOffIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RichTextEditor } from '../RichTextEditor';
import { AttachmentPicker } from './AttachmentPicker';
import { HASHTAG_SUGGESTIONS, SCREEN_LOCATIONS, SCREEN_DURATIONS } from '../../data/communications';
import { getReach } from '../../utils/audience';
import { resolvedLength } from '../../utils/variables';
import { AiTips } from './AiTips';
import { generateForChannel, getChannelTips, isChannelEmpty, type AiContext, type AiTip } from '../../utils/aiAssist';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}
function Switch({
  checked,
  onChange,
  label
}: SwitchProps) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-2 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3923" data-mp="xorukw84uod9">
      <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3924" data-mp="a0ibi22b0ept" />
    </button>;
}
interface ChannelEditorProps {
  channels: ChannelsState;
  onToggle: (key: keyof ChannelsState) => void;
  onUpdate: <K extends keyof ChannelsState>(key: K, patch: Partial<ChannelsState[K]>) => void;
  audienceTotal: number;
  /** Variables insertable in every channel's content. */
  variables: NoticeVariable[];
  /** Language the notice content is written in — drives resolved counters. */
  contentLang: 'fr' | 'en';
  /** Switches which language version of the content is being edited. */
  onContentLangChange: (value: 'fr' | 'en') => void;
  /** Whether each language version already holds content. */
  langStatus: {
    fr: boolean;
    en: boolean;
  };
  /** True when one version is written and the other is still empty. */
  canGenerateOtherLang: boolean;
  /** Writes the empty version from the one already completed. */
  onGenerateOtherLang: () => void;
  /** Everything the AI assistant needs to draft and review content. */
  aiContext: AiContext;
}
export function ChannelEditor({
  channels,
  onToggle,
  onUpdate,
  audienceTotal,
  variables,
  contentLang,
  onContentLangChange,
  langStatus,
  canGenerateOtherLang,
  onGenerateOtherLang,
  aiContext
}: ChannelEditorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  /** Tip whose target text is currently highlighted in an editor. */
  const [activeTip, setActiveTip] = useState<AiTip | null>(null);
  const selectTip = (tip: AiTip) => {
    if (!tip.highlight) return;
    setActiveTip(prev => prev?.id === tip.id ? null : tip);
  };

  /** Highlight to hand to a channel's main editor, if a tip points at it. */
  const highlightFor = (key: keyof ChannelsState) => activeTip && activeTip.id.startsWith(`${key}.`) ? activeTip.highlight : null;

  /** Drafts the whole channel — offered from its main editor while empty. */
  const generate = (key: keyof ChannelsState) => {
    onUpdate(key, generateForChannel(key, aiContext) as never);
    setActiveTip(null);
  };

  /** Generation handler for a channel's main editor, only while it is empty. */
  const generateFor = (key: keyof ChannelsState) => isChannelEmpty(key, channels) ? () => generate(key) : undefined;
  const card = (key: keyof ChannelsState, Icon: typeof MailIcon, title: string, subtitle: string, reachPercent: number, reachTone: 'good' | 'medium', body: React.ReactNode) => {
    const active = channels[key].active;
    const reach = getReach(audienceTotal, reachPercent, locale);
    return <div className="bg-white rounded-xl border-[0.5px] border-slate-200 overflow-hidden" data-id="element-3925" data-mp="i91y1owp4l5f">
        <div className="flex items-center justify-between p-4 bg-white gap-4" data-id="element-3926" data-mp="n626u7kxk208">
          <div className="flex items-center gap-4 min-w-0" data-id="element-3927" data-mp="s5n5si03oacy">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-50 text-slate-400'}`} data-id="element-3928" data-mp="s43pireg1zb6">
              <Icon className="w-5 h-5" data-id="element-3929" data-mp="15xuwpt4pd73q" />
            </div>
            <div className="min-w-0" data-id="element-3930" data-mp="15zedrly6nm6k">
              <h3 className="text-base font-medium text-slate-900" data-id="element-3931" data-mp="1awel006rquz9">{title}</h3>
              <p className="text-sm text-slate-500 truncate" data-id="element-3932" data-mp="1aubr352hwhp9">{subtitle}</p>
            </div>
            <div className="ml-4 hidden md:flex items-center gap-2 flex-shrink-0" data-id="element-3933" data-mp="2vccf6vo39aee">
              <span className="text-sm text-slate-600" data-id="element-3934" data-mp="2qeawibukrd3g">
                {reach.count} {t('citoyens', 'citizens')}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${reachTone === 'good' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`} data-id="element-3935" data-mp="2hf7b317xqlls">
                {reach.percent} % {t('de portée', 'reach')}
              </span>
            </div>
          </div>
          <Switch checked={active} onChange={() => onToggle(key)} label={title} data-id="element-3936" data-mp="30cqr4ost94hp" />
        </div>
        <AnimatePresence data-id="element-3937" data-mp="1pw528gnqds6">
          {active && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-3938" data-mp="3t1fs8ntv99tg">
              <div className="p-6 pt-4 border-t border-slate-100 space-y-4 bg-slate-50/50" data-id="element-3939" data-mp="6zynktvwzdoo">
                {body}

                <AiTips tips={getChannelTips(key, aiContext)} activeTipId={activeTip?.id ?? null} onSelect={selectTip} data-id="element-3940" data-mp="b1adl0ipa0xe" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <section className="space-y-6" data-id="element-3941" data-mp="3k94fui9tt1h2">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-3942" data-mp="3k9w9hnjv4p4p">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-3943" data-mp="3o9evkmnf7694">
          {t('Canaux de diffusion', 'Distribution channels')}
        </h2>

        {/* Language the notice CONTENT is written in — switches every channel
            at once, and is never the interface language. */}
        <div className="flex items-center gap-2" data-id="element-3944" data-mp="370m59tarhnru">
          <span className="text-sm font-medium text-slate-600" data-id="element-3945" data-mp="32y7n7bex2s1m">
            {t('Langue du contenu :', 'Content language:')}
          </span>
          <div role="group" aria-label={t('Langue du contenu', 'Content language')} className="inline-flex p-0.5 rounded-lg bg-slate-100 border border-slate-200" data-id="element-3946" data-mp="3gqnpg9zzum1h">
            {([{
            id: 'fr',
            label: 'FR',
            full: t('Français', 'French')
          }, {
            id: 'en',
            label: 'EN',
            full: t('Anglais', 'English')
          }] as const).map(l => <button key={l.id} type="button" onClick={() => onContentLangChange(l.id)} aria-pressed={contentLang === l.id} title={langStatus[l.id] ? l.full : `${l.full} — ${t('version vide', 'empty version')}`} className={`relative px-3 py-1.5 text-sm font-bold rounded-md transition-colors ${contentLang === l.id ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3947" data-mp="3gq4pjns7gn1">
                {l.label}
                {!langStatus[l.id] && <span aria-hidden="true" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-3948" data-mp="whc21lxtls12" />}
              </button>)}
          </div>
        </div>
      </div>

      {/* One version is written, the other is untouched — offer to write it. */}
      {canGenerateOtherLang && <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl bg-indigo-50/70 border border-indigo-100" data-id="element-3949" data-mp="2gwxl2zzsms6y">
          <p className="text-sm text-slate-700 min-w-0" data-id="element-3950" data-mp="2lugyxyskx2yd">
            <span className="font-semibold text-slate-900" data-id="element-3951" data-mp="a85co01jg9xw">
              {contentLang === 'fr' ? t('La version anglaise est vide.', 'The English version is empty.') : t('La version française est vide.', 'The French version is empty.')}
            </span>{' '}
            {t('ATNA peut la rédiger à partir du contenu déjà saisi.', 'ATNA can write it from the content already entered.')}
          </p>
          <button type="button" onClick={onGenerateOtherLang} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4F46E5] text-white text-sm font-semibold hover:bg-[#4338CA] transition-colors flex-shrink-0" data-id="element-3952" data-mp="3nl8fhlnypgyk">
            <SparklesIcon className="w-4 h-4" data-id="element-3953" data-mp="38rloyn2p7p5y" />
            {contentLang === 'fr' ? t('Générer la version anglaise', 'Generate the English version') : t('Générer la version française', 'Generate the French version')}
          </button>
        </div>}
      <div className="space-y-4" data-id="element-3954" data-mp="2b43reg3e0bzb">
        {/* EMAIL */}
        {card('email', MailIcon, t('Courriel', 'Email'), t("Lettre d'information détaillée", 'Detailed newsletter'), 82, 'good', <>
            <div data-id="element-3955" data-mp="2g485jzgtoumd">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3956" data-mp="2g3omgfffs06r">
                {t('Objet du courriel', 'Email subject')}
              </label>
              <RichTextEditor value={channels.email.subject} onChange={subject => onUpdate('email', {
            subject
          })} minHeight={40} variablesOnly singleLine variables={variables} ariaLabel={t('Objet du courriel', 'Email subject')} placeholder={t('Objet…', 'Subject…')} data-id="element-3957" data-mp="1scawgg4rcgoy" />
            </div>

            <div data-id="element-3958" data-mp="14pkx5mjsn081">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3959" data-mp="14o027vtkhf9n">
                {t('Contenu', 'Content')}
              </label>
              <RichTextEditor value={channels.email.body} onChange={body => onUpdate('email', {
            body
          })} minHeight={180} allowImages variables={variables} highlight={highlightFor('email')} onGenerate={generateFor('email')} ariaLabel={t('Contenu du courriel', 'Email content')} placeholder={t('Rédigez le contenu détaillé ici…', 'Write the detailed content here…')} data-id="element-3960" data-mp="3gabohukly8a4" />
            </div>

            <AttachmentPicker attachments={channels.email.attachments} onChange={attachments => onUpdate('email', {
          attachments
        })} data-id="element-3961" data-mp="3q4amchdnf5w9" />
          </>)}

        {/* SMS */}
        {card('sms', MessageSquareIcon, t('SMS', 'SMS'), t('Message texte court et urgent', 'Short, urgent text message'), 95, 'good', <div data-id="element-3962" data-mp="k6ubpjbdw8zd">
            <div className="flex justify-between items-end mb-1" data-id="element-3963" data-mp="k6auy5xgzqmx">
              <label className="block text-sm font-medium text-slate-700" data-id="element-3964" data-mp="k96rpqxl099s">
                {t('Message SMS', 'SMS message')}
              </label>
              <span className={`text-xs ${resolvedLength(channels.sms.text, variables, contentLang) > 160 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3965" data-mp="ahsuxqx0lnkc">
                {resolvedLength(channels.sms.text, variables, contentLang)} / 160
              </span>
            </div>
            <RichTextEditor value={channels.sms.text} onChange={text => onUpdate('sms', {
          text
        })} minHeight={72} variablesOnly variables={variables} highlight={highlightFor('sms')} onGenerate={generateFor('sms')} ariaLabel={t('Message SMS', 'SMS message')} placeholder={t('Message texte court…', 'Short text message…')} data-id="element-3966" data-mp="3no2r7win2wqx" />
            <p className="mt-1.5 text-xs text-slate-400" data-id="element-3967" data-mp="1ycpgg1bxg7bo">
              {t('Le compteur reflète la longueur finale, variables remplacées.', 'The counter reflects the final length, with variables replaced.')}
            </p>
          </div>)}

        {/* VOICE */}
        {card('voice', PhoneIcon, t('Voix automatisée', 'Automated voice'), t('Appel téléphonique généré par IA', 'AI-generated phone call'), 45, 'medium', <>
            <div data-id="element-3968" data-mp="1pf58yanh1esm">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3969" data-mp="1pfos1uouy988">
                {t('Voix', 'Voice')}
              </label>
              <select value={channels.voice.voice} onChange={e => onUpdate('voice', {
            voice: e.target.value
          })} className="block w-full bg-white border border-slate-200 px-3 py-2 text-sm rounded-md focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-3970" data-mp="30taf9ab5q0r4">
                <option value="femme" data-id="element-3971" data-mp="mmkrp7yge6rx">
                  {t('Voix féminine (Standard)', 'Female voice (Standard)')}
                </option>
                <option value="homme" data-id="element-3972" data-mp="horjyahi54pj">{t('Voix masculine', 'Male voice')}</option>
              </select>
            </div>
            <div data-id="element-3973" data-mp="rkujr9ovsr9b">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3974" data-mp="rle4ey5x1tr1">
                {t("Script de l'appel", 'Call script')}
              </label>
              <RichTextEditor value={channels.voice.script} onChange={script => onUpdate('voice', {
            script
          })} minHeight={80} variablesOnly variables={variables} highlight={highlightFor('voice')} onGenerate={generateFor('voice')} ariaLabel={t("Script de l'appel", 'Call script')} placeholder={t('Bonjour, ceci est un message important de la ville…', 'Hello, this is an important message from the city…')} data-id="element-3975" data-mp="15dtz9bonylgv" />
            </div>
          </>)}

        {/* PUSH */}
        {card('push', BellIcon, t('Notification Push', 'Push notification'), t("Alerte sur l'application mobile", 'Alert in the mobile app'), 88, 'good', <>
            <div data-id="element-3976" data-mp="1188d0wge7hcd">
              <div className="flex justify-between items-end mb-1" data-id="element-3977" data-mp="118qdk5a38hq3">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3978" data-mp="116do77203530">
                  {t('Titre de la notification', 'Notification title')}
                </label>
                <span className={`text-xs ${resolvedLength(channels.push.title, variables, contentLang) > 50 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3979" data-mp="s8brv4t7p0m2">
                  {resolvedLength(channels.push.title, variables, contentLang)} / 50
                </span>
              </div>
              <RichTextEditor value={channels.push.title} onChange={title => onUpdate('push', {
            title
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('push')} onGenerate={generateFor('push')} ariaLabel={t('Titre de la notification', 'Notification title')} placeholder={t('Titre court…', 'Short title…')} data-id="element-3980" data-mp="2p6952aw8t8xj" />
            </div>
            <div data-id="element-3981" data-mp="1whto3itjwgjk">
              <div className="flex justify-between items-end mb-1" data-id="element-3982" data-mp="1wid772uxtaz6">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3983" data-mp="1sfyocir9qb7w">
                  {t('Message', 'Message')}
                </label>
                <span className={`text-xs ${resolvedLength(channels.push.message, variables, contentLang) > 100 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3984" data-mp="7yz70kfnriuo">
                  {resolvedLength(channels.push.message, variables, contentLang)} / 100
                </span>
              </div>
              <RichTextEditor value={channels.push.message} onChange={message => onUpdate('push', {
            message
          })} minHeight={56} variablesOnly variables={variables} ariaLabel={t('Message', 'Message')} placeholder={t("Détails de l'alerte…", 'Alert details…')} data-id="element-3985" data-mp="qnw4ex6cqrh4" />
            </div>
          </>)}

        {/* WEBSITE */}
        {card('website', GlobeIcon, t('Site web', 'Website'), t("Bannière d'alerte sur le portail", 'Alert banner on the portal'), 100, 'good', <>
            <div data-id="element-3986" data-mp="184tc1qboaft3">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3987" data-mp="185cs11hriu4h">
                {t('Texte de la bannière', 'Banner text')}
              </label>
              <RichTextEditor value={channels.website.bannerText} onChange={bannerText => onUpdate('website', {
            bannerText
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('website')} onGenerate={generateFor('website')} ariaLabel={t('Texte de la bannière', 'Banner text')} placeholder={t('Alerte importante…', 'Important alert…')} data-id="element-3988" data-mp="1gxo8pvn2r379" />
            </div>
            <div data-id="element-3989" data-mp="2enrkhysrsral">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3990" data-mp="29n3rj7kze0dq">
                {t("Label du bouton d'action", 'Action button label')}
              </label>
              <RichTextEditor value={channels.website.ctaLabel} onChange={ctaLabel => onUpdate('website', {
            ctaLabel
          })} minHeight={40} variablesOnly singleLine variables={variables} ariaLabel={t("Label du bouton d'action", 'Action button label')} placeholder={t('En savoir plus', 'Learn more')} data-id="element-3991" data-mp="3rmkxc3cmha5l" />
            </div>
          </>)}

        {/* SOCIAL */}
        {card('social', Share2Icon, t('Réseaux sociaux', 'Social media'), t('Publication Facebook et X', 'Facebook and X post'), 60, 'good', <>
            <div data-id="element-3992" data-mp="y27gc91n4msb">
              <div className="flex justify-between items-end mb-1" data-id="element-3993" data-mp="y1nx8p097scp">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3994" data-mp="12snwi0wuimid">
                  {t('Texte de la publication', 'Post text')}
                </label>
                <span className="text-xs text-slate-400" data-id="element-3995" data-mp="1j2on080pfgik">
                  {resolvedLength(channels.social.postText, variables, contentLang)}{' '}
                  {t('caractères', 'characters')}
                </span>
              </div>
              <RichTextEditor value={channels.social.postText} onChange={postText => onUpdate('social', {
            postText
          })} minHeight={130} allowImages variables={variables} highlight={highlightFor('social')} onGenerate={generateFor('social')} ariaLabel={t('Texte de la publication', 'Post text')} placeholder={t('Quoi de neuf ?', "What's new?")} data-id="element-3996" data-mp="1ebeyxbut0l8s" />
              <p className="mt-1.5 text-xs text-slate-400" data-id="element-3997" data-mp="2lp9t0h43tkjz">
                {t('Les publications avec image obtiennent en moyenne 2× plus d’interactions.', 'Posts with an image get on average 2× more engagement.')}
              </p>
            </div>

            <div data-id="element-3998" data-mp="a26lo1h3ec7h">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3999" data-mp="a2oeeo4jq8an">
                {t('Suggestions de hashtags', 'Hashtag suggestions')}
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-4000" data-mp="j2l6z0u23raz">
                {HASHTAG_SUGGESTIONS.map(tag => <button key={tag} type="button" onClick={() => onUpdate('social', {
              postText: `${channels.social.postText}${channels.social.postText ? ' ' : ''}${tag}`
            })} className="px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-600 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-4001" data-mp="xqpseckhugxg">
                    {tag}
                  </button>)}
              </div>
            </div>
          </>)}

        {/* PUBLIC SCREENS */}
        {card('screens', MonitorIcon, t('Écrans publics', 'Public screens'), t('Affichage dans les bâtiments municipaux', 'Signage in municipal buildings'), 35, 'medium', <>
            <div data-id="element-4002" data-mp="1qevp6lbexilk">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4003" data-mp="1qfdue7g1z75m">
                {t('Titre affiché', 'Displayed headline')}
              </label>
              <RichTextEditor value={channels.screens.headline} onChange={headline => onUpdate('screens', {
            headline
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('screens')} onGenerate={generateFor('screens')} ariaLabel={t('Titre affiché', 'Displayed headline')} placeholder={t('Avis important', 'Important notice')} data-id="element-4004" data-mp="2056twa2x7vti" />
              <p className="mt-1.5 text-xs text-slate-400" data-id="element-4005" data-mp="3tmw1yxoxpodl">
                {t('Lisible à 3 mètres : visez moins de 8 mots.', 'Readable from 3 metres: aim for fewer than 8 words.')}
              </p>
            </div>

            <div data-id="element-4006" data-mp="l8vto2kv756n">
              <div className="flex justify-between items-end mb-1" data-id="element-4007" data-mp="l7ce6e718y5x">
                <label className="block text-sm font-medium text-slate-700" data-id="element-4008" data-mp="h6iq9ktlbjd1">
                  {t('Message', 'Message')}
                </label>
                <span className="text-xs text-slate-400" data-id="element-4009" data-mp="cehgq6nt46qp">
                  {resolvedLength(channels.screens.bodyText, variables, contentLang)}
                  {' / 180 '}
                  {t('caractères', 'characters')}
                </span>
              </div>
              <RichTextEditor value={channels.screens.bodyText} onChange={bodyText => onUpdate('screens', {
            bodyText
          })} minHeight={80} variablesOnly variables={variables} ariaLabel={t('Message', 'Message')} placeholder={t('Une ou deux phrases courtes…', 'One or two short sentences…')} data-id="element-4010" data-mp="jpvvq6woqag7" />
            </div>

            <div data-id="element-4011" data-mp="17fq7sm96juze">
              <label htmlFor="screen-duration" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4012" data-mp="17g7yz4gzjj0g">
                {t('Durée d’affichage', 'Slide duration')}
              </label>
              <select id="screen-duration" value={channels.screens.duration} onChange={e => onUpdate('screens', {
            duration: e.target.value
          })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4013" data-mp="ym3er3wcx5tw">
                {SCREEN_DURATIONS.map(option => <option key={option.value} value={option.value} data-id="element-4014" data-mp="2a7ivsks2phcz">
                    {option.label[lang]}
                  </option>)}
              </select>
            </div>

            <div data-id="element-4015" data-mp="1hj4zqakjov42">
              <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap" data-id="element-4016" data-mp="1hkof7yydn24s">
                <label className="block text-sm font-medium text-slate-700" data-id="element-4017" data-mp="1djcxeflqvl75">
                  {t('Écrans ciblés', 'Target screens')}
                </label>
                <span className="text-xs text-slate-500" data-id="element-4018" data-mp="1mizp29oo9cn0">
                  {(() => {
                const views = SCREEN_LOCATIONS.filter(s => s.online && channels.screens.locations.includes(s.id)).reduce((sum, s) => sum + s.dailyViews, 0);
                return t(`≈ ${views.toLocaleString(locale)} passages par jour`, `≈ ${views.toLocaleString(locale)} passers-by per day`);
              })()}
                </span>
              </div>
              <div className="space-y-2" data-id="element-4019" data-mp="3expvs2539boa">
                {SCREEN_LOCATIONS.map(screen => {
              const checked = channels.screens.locations.includes(screen.id);
              return <label key={screen.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white cursor-pointer transition-colors ${checked ? 'border-[#4F46E5] ring-1 ring-[#4F46E5]/20' : 'border-slate-200 hover:bg-slate-50'} ${screen.online ? '' : 'opacity-60'}`} data-id="element-4020" data-mp="2r9y6zz2d3664">
                      <input type="checkbox" checked={checked} disabled={!screen.online} onChange={() => onUpdate('screens', {
                  locations: checked ? channels.screens.locations.filter(id => id !== screen.id) : [...channels.screens.locations, screen.id]
                })} className="w-4 h-4 rounded border-slate-300 text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-4021" data-mp="28i5m6rxg4r9h" />
                      <span className="min-w-0 flex-1" data-id="element-4022" data-mp="18bjreruimqsp">
                        <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-4023" data-mp="13cyrktj6e94s">
                          {screen.place[lang]}
                        </span>
                        <span className="block text-xs text-slate-500 truncate" data-id="element-4024" data-mp="ubhbhb5rq7g3">
                          {screen.label[lang]}
                          {' · '}
                          {screen.dailyViews.toLocaleString(locale)}{' '}
                          {t('passages/jour', 'views/day')}
                        </span>
                      </span>
                      {!screen.online && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium flex-shrink-0" data-id="element-4025" data-mp="2d940me25tz3r">
                          <WifiOffIcon className="w-3 h-3" data-id="element-4026" data-mp="1zgebw1v8yg2y" />
                          {t('hors ligne', 'offline')}
                        </span>}
                    </label>;
            })}
              </div>
            </div>
          </>)}
      </div>
    </section>;
}